import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
	deleteBroadcast,
	getBroadcasts,
	resumeBroadcast,
} from "../../api/apiServiceFetch";
import {
	showActionError,
	showActionSuccess,
} from "../../utils/showActionError";

const statusColors = {
	pending: "default",
	in_progress: "info",
	completed: "success",
	aborted: "error",
};

const statusLabels = {
	pending: "ожидает",
	in_progress: "выполняется",
	completed: "завершена",
	aborted: "прервана",
};

const mediaLabels = {
	0: "Текст",
	1: "Фото",
	2: "Видео",
	3: "Аудио",
	4: "Голос",
	5: "Документ",
	6: "Копия",
};

const audienceLabels = {
	0: "Всем",
	1: "Новые",
	2: "Редакт.",
	3: "Фейл",
	4: "Заморозки",
	5: "Не покупали",
	6: "Без подписки",
	9: "Удалённые",
};

const sexLabels = {
	0: "Все",
	1: "Муж",
	2: "Жен",
};

const formatDate = (value) => {
	if (!value) return "-";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";

	return date.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const AUTO_REFRESH_MS = 5000;

const Broadcasts = () => {
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [resumingId, setResumingId] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [error, setError] = useState(null);
	const pollTimerRef = useRef(null);

	const load = useCallback(async ({ silent } = {}) => {
		if (!silent) setLoading(true);
		setError(null);

		try {
			const data = await getBroadcasts(100, 0);
			setJobs(Array.isArray(data) ? data : []);
		} catch (e) {
			console.error("getBroadcasts failed:", e);
			setError(e?.message || "Не удалось загрузить рассылки");
		} finally {
			if (!silent) setLoading(false);
		}
	}, []);

	// Initial load.
	useEffect(() => {
		load();
	}, [load]);

	// Auto-refresh while any job is still running (pending > 0 or status==in_progress).
	useEffect(() => {
		const stillRunning = jobs.some(
			(j) => (j.pending ?? 0) > 0 || j.status === "in_progress",
		);

		if (!stillRunning) {
			if (pollTimerRef.current) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
			return undefined;
		}

		if (pollTimerRef.current) return undefined;

		pollTimerRef.current = setInterval(() => {
			load({ silent: true });
		}, AUTO_REFRESH_MS);

		return () => {
			if (pollTimerRef.current) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
		};
	}, [jobs, load]);

	const handleResume = async (job) => {
		if (!job?.id) return;

		setResumingId(job.id);

		try {
			await resumeBroadcast(job.id);
			showActionSuccess(
				`Возобновление запланировано для рассылки #${job.id}`,
			);
			setTimeout(() => load({ silent: true }), 1500);
		} catch (e) {
			showActionError(e, "Не удалось возобновить рассылку");
		} finally {
			setResumingId(null);
		}
	};

	const handleDelete = async (job) => {
		if (!job?.id) return;
		if (!window.confirm(`Удалить рассылку #${job.id}? Действие необратимо.`))
			return;

		setDeletingId(job.id);

		try {
			await deleteBroadcast(job.id);
			setJobs((prev) => prev.filter((j) => j.id !== job.id));
		} catch (e) {
			showActionError(e, "Ошибка удаления рассылки");
		} finally {
			setDeletingId(null);
		}
	};

	const columns = [
		{ field: "id", headerName: "ID", width: 80 },
		{
			field: "type",
			headerName: "Аудитория",
			width: 130,
			valueGetter: (value) => audienceLabels[value] ?? value,
		},
		{
			field: "sex",
			headerName: "Пол",
			width: 90,
			valueGetter: (value) => sexLabels[value] ?? value,
		},
		{
			field: "media",
			headerName: "Медиа",
			width: 110,
			valueGetter: (value) => mediaLabels[value] ?? value,
		},
		{
			field: "status",
			headerName: "Статус",
			width: 140,
			renderCell: ({ row }) => {
				const effective =
					row.status !== "aborted" && row.pending === 0
						? "completed"
						: row.status;

				return (
					<Chip
						size="small"
						label={statusLabels[effective] ?? effective}
						color={statusColors[effective] ?? "default"}
					/>
				);
			},
		},
		{ field: "totalUsers", headerName: "Всего", width: 90 },
		{ field: "success", headerName: "✅", width: 80 },
		{ field: "failed", headerName: "❌", width: 80 },
		{ field: "deleted", headerName: "🗑️", width: 80 },
		{ field: "pending", headerName: "⏳", width: 80 },
		{
			field: "createdAt",
			headerName: "Создан",
			width: 170,
			valueGetter: (value) => formatDate(value),
		},
		{
			field: "messagePreview",
			headerName: "Сообщение",
			flex: 1,
			minWidth: 200,
			valueGetter: (value) => value ?? "",
		},
		{
			field: "actions",
			headerName: "Действия",
			width: 220,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => {
				const canResume = (row.pending ?? 0) > 0;
				const isResuming = resumingId === row.id;
				const isDeleting = deletingId === row.id;

				return (
					<Stack direction="row" spacing={1}>
						{canResume && (
							<Button
								size="small"
								variant="contained"
								disabled={isResuming || isDeleting}
								onClick={() => handleResume(row)}
							>
								{isResuming ? "..." : "Возобновить"}
							</Button>
						)}
						<Button
							size="small"
							variant="outlined"
							color="error"
							disabled={isDeleting || isResuming}
							onClick={() => handleDelete(row)}
						>
							{isDeleting ? "..." : "Удалить"}
						</Button>
					</Stack>
				);
			},
		},
	];

	const runningCount = jobs.filter(
		(j) => (j.pending ?? 0) > 0 || j.status === "in_progress",
	).length;

	return (
		<Box sx={{ p: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Box>
					<Typography variant="h5">Рассылки</Typography>
					{runningCount > 0 && (
						<Typography variant="caption" color="text.secondary">
							Активных: {runningCount} (автообновление каждые 5 сек)
						</Typography>
					)}
				</Box>
				<Button variant="outlined" onClick={() => load()} disabled={loading}>
					{loading ? "Загрузка..." : "Обновить"}
				</Button>
			</Box>

			{error && (
				<Typography color="error" sx={{ mb: 2 }}>
					{error}
				</Typography>
			)}

			<Box sx={{ width: "100%" }}>
				<DataGrid
					rows={jobs}
					columns={columns}
					getRowId={(row) => row.id}
					loading={loading}
					autoHeight
					pageSizeOptions={[25, 50, 100]}
					initialState={{
						pagination: { paginationModel: { pageSize: 25 } },
					}}
					disableRowSelectionOnClick
				/>
			</Box>
		</Box>
	);
};

export default Broadcasts;
